import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Menu, LogOut, LayoutDashboard, FileText, Upload, Flag, Shield,
  Calendar, Settings, Users as UsersIcon, ShoppingCart, Droplets, Wallet,
  BarChart3, RefreshCw, Inbox, Barcode, Clock, UserPlus, Fuel, Sparkles,
  Home, Calculator, Download, Trash2,
} from "lucide-react";
import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { classNames } from "@/lib/format";

// Short role tags surfaced next to each menu item so a filler / cashier can
// instantly see "yes, this is for me" without training. Owner is intentionally
// NOT called "owner" in the UI — see the `Highest` badge.
const SHORT = (rs) => {
  if (rs.includes("owner") && rs.length <= 3) return "Highest";
  if (rs.includes("owner") && !rs.includes("filler") && !rs.includes("cashier")) return "Managers";
  if (rs.includes("filler") && rs.includes("cashier") && rs.length >= 5) return "Everyone";
  if (rs.includes("filler") && !rs.includes("manager")) return "Filler";
  if (rs.includes("cashier") && !rs.includes("filler")) return "Cashier +";
  if (rs.includes("assistant_manager")) return "Manager +";
  return "";
};

const NAV = [
  // --- Everyone ---
  { to: "/", label: "Home", icon: Home, section: "start", roles: ["filler", "cashier", "manager", "assistant_manager", "developer", "admin", "owner"] },

  // --- Cashier View: EXACTLY Data Entry + Summary for filler/cashier.
  //     Managers+ see these plus their own extra items further down. ---
  // Phase-3 A6: nav role tags — Data Entry, Summary, and Upload are the
  // cashier's tools. Everyone-above-cashier can still access them, but the
  // tag says "Cashier" so the badge signals "this is the cashier's job".
  { to: "/shifts/new", label: "Data Entry", icon: Fuel, section: "shift", primary: true, displayTag: "Cashier",
    roles: ["filler", "cashier", "manager", "assistant_manager", "developer", "admin", "owner"] },
  { to: "/shifts", label: "Summary", icon: FileText, section: "shift", featured: true, displayTag: "Cashier",
    roles: ["filler", "cashier", "manager", "assistant_manager", "developer", "admin", "owner"] },
  { to: "/uploads", label: "Upload", icon: Upload, section: "shift", displayTag: "Cashier",
    roles: ["cashier", "manager", "assistant_manager", "developer", "admin", "owner"] },
  { to: "/reconciliation", label: "Reconciliation", icon: BarChart3, section: "shift",
    roles: ["manager", "assistant_manager", "developer", "admin", "owner"] },

  // --- Red Flags — manager+ only. ---
  { to: "/flags", label: "Red Flags Queue", icon: Flag, section: "flags",
    roles: ["manager", "assistant_manager", "developer", "admin", "owner"] },
  { to: "/fraud", label: "Red Flags Overview", icon: Shield, section: "flags",
    roles: ["manager", "assistant_manager", "developer", "admin", "owner"] },
  { to: "/review-queue", label: "Review Queue", icon: Inbox, section: "flags",
    roles: ["manager", "assistant_manager", "developer", "admin", "owner"] },

  // --- Manager area ---
  { to: "/analytics", label: "Analytics", icon: LayoutDashboard, section: "manager",
    roles: ["manager", "assistant_manager", "developer", "admin", "owner"] },
  { to: "/manager-daily", label: "Manager Daily", icon: Calendar, section: "manager",
    roles: ["manager", "assistant_manager", "developer", "admin", "owner"] },
  { to: "/fuel-purchase", label: "Fuel Purchase (PO → Invoice)", icon: ShoppingCart, section: "manager",
    roles: ["manager", "developer", "admin", "owner"] },
  { to: "/dip-to-dip", label: "Dip-to-Dip", icon: BarChart3, section: "manager",
    roles: ["manager", "assistant_manager", "developer", "admin", "owner"] },
  { to: "/lubricants", label: "Lubricants", icon: Droplets, section: "manager",
    roles: ["manager", "assistant_manager", "developer", "admin", "owner"] },
  { to: "/lubricant-labels", label: "Lub Barcodes", icon: Barcode, section: "manager",
    roles: ["manager", "developer", "admin", "owner"] },
  { to: "/debtors", label: "Debtor Ledger", icon: Wallet, section: "manager",
    roles: ["manager", "assistant_manager", "developer", "admin", "owner"] },
  { to: "/debtors-pro", label: "Debtors — Outstanding & Interest", icon: Wallet, section: "manager",
    roles: ["manager", "assistant_manager", "developer", "admin", "owner"] },
  { to: "/sales", label: "Sales Summary", icon: BarChart3, section: "manager",
    roles: ["manager", "assistant_manager", "developer", "admin", "owner"] },
  { to: "/attendance", label: "Attendance", icon: Clock, section: "manager",
    roles: ["owner", "developer", "admin", "manager", "assistant_manager"] },
  { to: "/payroll", label: "Payroll / Salary Statement", icon: Wallet, section: "manager",
    roles: ["owner", "developer", "admin", "manager"] },
  { to: "/employee-requests", label: "Employee change requests", icon: UserPlus, section: "manager",
    roles: ["owner", "developer", "admin", "manager", "assistant_manager"] },
  { to: "/interest", label: "Interest & Credit", icon: Calculator, section: "manager",
    roles: ["manager", "developer", "admin", "owner"] },

  // --- Exports & owner tools ---
  { to: "/legacy-export", label: "Legacy Export", icon: Download, section: "owner",
    roles: ["manager", "developer", "admin", "owner"] },
  { to: "/masters", label: "Masters", icon: Settings, section: "owner", roles: ["developer", "admin", "owner"] },
  { to: "/users", label: "Users", icon: UsersIcon, section: "owner", roles: ["developer", "admin", "owner"] },
  { to: "/config", label: "Settings", icon: Settings, section: "owner", roles: ["owner", "developer", "admin"] },
  { to: "/integrations", label: "Integrations", icon: RefreshCw, section: "owner", roles: ["owner", "developer", "admin"] },
  { to: "/invites", label: "Invites", icon: UserPlus, section: "owner", roles: ["owner", "developer", "admin", "manager"] },
  { to: "/wipe", label: "Reset test data", icon: Trash2, section: "owner", roles: ["owner"] },
  { to: "/tour", label: "How this works", icon: Sparkles, section: "owner",
    roles: ["cashier", "manager", "assistant_manager", "developer", "admin", "owner"] },
];

const SECTIONS = [
  { id: "start", label: "" },
  { id: "shift", label: "Cashier View" },
  { id: "flags", label: "Manager View — Red flags" },
  { id: "manager", label: "Manager View" },
  { id: "owner", label: "Admin View" },
];

const ROLE_LABELS = {
  owner: "Admin View",
  developer: "Admin View",
  admin: "Admin View",
  manager: "Manager View",
  assistant_manager: "Manager View",
  cashier: "Cashier View",
  filler: "Cashier View",
};

export default function AppShell() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [attendance, setAttendance] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    if (!user) return;
    api.get("/team/attendance/me").then((r) => setAttendance(r.data)).catch(() => {});
  }, [user]);

  const clockIn = async () => {
    try { const r = await api.post("/team/attendance/clock_in", {}); setAttendance(r.data); toast.success("Clocked in ✓"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const clockOut = async () => {
    try { const r = await api.post("/team/attendance/clock_out"); setAttendance(null); toast.success(`Clocked out · ${r.data.duration_min}m`); }
    catch (e) { toast.error(apiError(e)); }
  };

  const items = NAV.filter((n) => n.roles.includes(user?.role));

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-white border-b-2 border-slate-300">
        <div className="mx-auto max-w-[1400px] px-3 py-2 flex items-center gap-3">
          <button
            data-testid="menu-toggle-btn"
            className="p-2 md:hidden hover:bg-slate-100 rounded-sm"
            onClick={() => setOpen(!open)}
          >
            <Menu size={22} strokeWidth={2.5} />
          </button>
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 bg-slate-900 text-white grid place-items-center font-bold rounded-sm">V</div>
            <div>
              <div className="font-bold tracking-tight text-slate-900 leading-none">VAS POS 2</div>
              <div className="text-xs text-slate-600 leading-none mt-1">Velkar Auto Service · Thane</div>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {attendance ? (
              <Button data-testid="clock-out-btn" onClick={clockOut} size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white text-xs h-8">
                <Clock size={12} className="mr-1" /> Clock Out
              </Button>
            ) : (
              <Button data-testid="clock-in-btn" onClick={clockIn} size="sm" variant="outline" className="border-slate-300 text-xs h-8">
                <Clock size={12} className="mr-1" /> Clock In
              </Button>
            )}
            <span data-testid="sync-status" className="hidden sm:inline-flex items-center gap-1 text-xs px-2 py-1 border border-emerald-300 bg-emerald-100 text-emerald-800 rounded-sm">
              <span className="h-2 w-2 rounded-full bg-emerald-600"></span>
              Saved ✓
            </span>
            <div className="hidden sm:block text-right">
              <div className="text-sm font-semibold text-slate-900 leading-none">{user?.name}</div>
              <div className="text-xs text-slate-600 mt-1 uppercase tracking-wider">{ROLE_LABELS[user?.role] || user?.role}</div>
            </div>
            <Button
              data-testid="logout-btn"
              variant="outline"
              size="sm"
              onClick={logout}
              className="border-slate-300"
            >
              <LogOut size={14} className="mr-1" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1400px] flex">
        {/* Sidebar */}
        <aside
          className={classNames(
            "fixed md:sticky md:top-[57px] top-[52px] left-0 z-30 h-[calc(100vh-52px)] md:h-[calc(100vh-57px)] w-64 bg-white border-r-2 border-slate-300 overflow-y-auto transition-transform",
            open ? "translate-x-0" : "-translate-x-full md:translate-x-0",
          )}
        >
          <nav className="p-2 flex flex-col gap-1">
            {SECTIONS.map((sec) => {
              const secItems = items.filter((n) => n.section === sec.id);
              if (secItems.length === 0) return null;
              return (
                <div key={sec.id} className="mt-2 first:mt-0">
                  {sec.label && (
                    <div className="px-2 pb-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                      {sec.label}
                    </div>
                  )}
                  {secItems.map((n) => {
                    const tag = n.displayTag || SHORT(n.roles);
                    return (
                      <NavLink
                        key={n.to}
                        to={n.to}
                        data-testid={`nav-${n.to.replace(/\//g, "") || "home"}`}
                        end={n.to === "/"}
                        onClick={() => setOpen(false)}
                        className={({ isActive }) => classNames(
                          "flex items-center gap-2 px-3 text-sm font-medium rounded-sm border-2",
                          n.primary ? "py-3 mt-1 mb-1" : n.featured ? "py-3" : "py-2",
                          n.primary && !isActive && "bg-emerald-600 text-white border-emerald-700 hover:bg-emerald-700",
                          n.featured && !isActive && "bg-amber-50 border-amber-400 text-amber-900 hover:bg-amber-100 shadow-sm",
                          isActive ? "bg-slate-900 text-white border-slate-900"
                            : (!n.primary && !n.featured && "border-transparent hover:bg-slate-100 text-slate-800"),
                        )}
                      >
                        <n.icon size={n.featured || n.primary ? 18 : 16} strokeWidth={n.featured || n.primary ? 2.5 : 2.25} />
                        <span className="flex-1 truncate">{n.label}</span>
                        {tag && (
                          <span
                            className={classNames(
                              "text-[9px] px-1.5 py-0.5 rounded-sm border font-semibold uppercase tracking-wider",
                              tag === "Cashier" && "border-blue-300 bg-blue-50 text-blue-800",
                              tag === "Filler" && "border-cyan-300 bg-cyan-50 text-cyan-800",
                              tag === "Cashier +" && "border-blue-300 bg-blue-50 text-blue-800",
                              tag === "Manager +" && "border-purple-300 bg-purple-50 text-purple-800",
                              tag === "Managers" && "border-purple-300 bg-purple-50 text-purple-800",
                              tag === "Highest" && "border-rose-300 bg-rose-50 text-rose-800",
                              tag === "Everyone" && "border-slate-300 bg-slate-100 text-slate-600",
                            )}
                          >
                            {tag}
                          </span>
                        )}
                      </NavLink>
                    );
                  })}
                </div>
              );
            })}
          </nav>
        </aside>

        {/* Main */}
        <main className="flex-1 p-3 md:p-6 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
